type WakeWordListener = (keyword: string) => void;

interface WakeWordKeyword {
  label: string;
  publicPath?: string;
  sensitivity?: number;
}

interface WakeWordInitOptions {
  accessKey?: string;
  keywords?: WakeWordKeyword[];
  language?: string;
}

const DEFAULT_KEYWORDS: WakeWordKeyword[] = [
  { label: 'hey-x', publicPath: '/models/hey-x.ppn', sensitivity: 0.65 },
  { label: 'hi-x', publicPath: '/models/hi-x.ppn', sensitivity: 0.65 },
  { label: 'hey-x-assist', publicPath: '/models/hey-x-assist.ppn', sensitivity: 0.65 },
  { label: 'hello-x', publicPath: '/models/hello-x.ppn', sensitivity: 0.65 },
];

const HEY_X_PATTERN = /(hey|hi|hello)\s+(x\s*assist|x|ex)|hey\s+oil|hey\s*x/i;

class WakeWordService {
  private listeners = new Set<WakeWordListener>();
  private isInitialized = false;
  private isListening = false;
  private usingFallback = false;
  private recognition: any = null;
  private porcupineWorker: any = null;
  private accessKey?: string;
  private keywordConfig: WakeWordKeyword[] = DEFAULT_KEYWORDS;
  private fallbackLanguage = 'en-US';

  async initialize(options: WakeWordInitOptions = {}) {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }

    this.accessKey = options.accessKey;
    this.keywordConfig = options.keywords?.length ? options.keywords : DEFAULT_KEYWORDS;
    this.fallbackLanguage = options.language || 'en-US';

    if (this.accessKey) {
      try {
        await this.initializePorcupine();
        this.isInitialized = true;
        return;
      } catch (error) {
      }
    }

    this.initializeSpeechFallback();
    this.isInitialized = true;
  }

  addListener(listener: WakeWordListener) {
    this.listeners.add(listener);
  }

  removeListener(listener: WakeWordListener) {
    this.listeners.delete(listener);
  }

  async start() {
    if (!this.isInitialized || this.isListening) {
      return;
    }

    if (this.porcupineWorker) {
      await this.startPorcupine();
      this.isListening = true;
      return;
    }

    if (this.recognition) {
      try {
        this.recognition.start();
        this.isListening = true;
      } catch (error) {
      }
    }
  }

  stop() {
    if (!this.isListening) {
      return;
    }

    if (this.porcupineWorker) {
      this.porcupineWorker.postMessage({ command: 'stop' });
      this.isListening = false;
      return;
    }

    if (this.recognition) {
      try {
        this.recognition.onend = null;
        this.recognition.stop();
      } catch (error) {
      }
    }

    this.isListening = false;
  }

  isActive(): boolean {
    return this.isListening;
  }

  private async initializePorcupine() {
    if (!this.accessKey) {
      throw new Error('Missing Picovoice access key');
    }

    try {
      const module = await import('@picovoice/porcupine-web');
      const Factory = (module as any).PorcupineWorkerFactory || (module as any).PorcupineWorker;

      if (!Factory) {
        throw new Error('Picovoice worker factory not available');
      }

      const factoryInstance = new Factory();
      this.porcupineWorker = await factoryInstance.create(
        this.accessKey,
        this.keywordConfig.map(keyword => ({
          publicPath: keyword.publicPath,
          label: keyword.label,
          sensitivity: keyword.sensitivity ?? 0.65,
        })),
        (detection: { label: string }) => {
          this.emit(detection.label);
        }
      );
    } catch (error) {
      throw error;
    }
  }

  private async startPorcupine() {
    if (!this.porcupineWorker) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(512, 1, 1);

    processor.onaudioprocess = (event) => {
      const input = event.inputBuffer.getChannelData(0);
      this.porcupineWorker.postMessage({ command: 'process', inputFrame: input });
    };

    source.connect(processor);
    processor.connect(audioContext.destination);
  }

  private initializeSpeechFallback() {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      return;
    }

    this.recognition = new SpeechRecognition();
    this.recognition.continuous = true;
    this.recognition.interimResults = false;
    this.recognition.lang = this.fallbackLanguage;

    this.recognition.onresult = (event: any) => {
      const transcript = event.results[event.results.length - 1][0].transcript.trim();
      if (HEY_X_PATTERN.test(transcript)) {
        this.emit('hey-x');
        this.temporarilyPause().catch(console.error);
      }
    };

    this.recognition.onerror = () => {
      if (!this.usingFallback) {
        this.initializeSpeechFallback();
      }
    };

    this.recognition.onend = () => {
      if (this.isListening) {
        try {
          this.recognition?.start();
        } catch (error) {
        }
      }
    };

    this.usingFallback = true;
  }

  private emit(keyword: string) {
    this.listeners.forEach(listener => listener(keyword));
  }

  async temporarilyPause(durationMs = 2000) {
    if (!this.isListening) return;
    this.stop();
    await new Promise(resolve => setTimeout(resolve, durationMs));
    await this.start();
  }
}

export const wakeWordService = new WakeWordService();
